// Pure helpers that derive the visual bracket tree from schedule data.
// Read-only: never mutates matches or progression state.
const PLACEHOLDER = /^(W_|L_|SEED_|POOL_)/;

export const isPlaceholder = (id) => !id || id === 'BYE' || PLACEHOLDER.test(id);

export function findFeeder(teamId, pool, beforeRound, kinds) {
  if (!teamId || teamId === 'BYE') return null;
  const ref = /^(W|L)_(.+)$/.exec(teamId);
  if (ref) {
    if (!kinds.includes(ref[1])) return null;
    const m = pool.find((x) => x.id === ref[2]);
    return m ? { match: m, kind: ref[1] } : null;
  }
  let best = null;
  pool.forEach((m) => {
    if ((m.round ?? 0) >= beforeRound) return;
    let kind = null;
    if (kinds.includes('W') && m.winner === teamId) kind = 'W';
    else if (kinds.includes('L') && m.loser === teamId) kind = 'L';
    if (kind && (!best || (m.round ?? 0) > (best.match.round ?? 0))) best = { match: m, kind };
  });
  return best;
}

export function buildTree(root, pool, kinds = ['W']) {
  const used = new Set();
  const make = (match) => {
    used.add(match.id);
    const node = { match, kids: [null, null], via: 'W' };
    [match.team_a, match.team_b].forEach((t, i) => {
      const f = findFeeder(t, pool, match.round ?? 0, kinds);
      if (f && !used.has(f.match.id)) {
        node.kids[i] = make(f.match);
        node.kids[i].via = f.kind;
      }
    });
    return node;
  };
  return { tree: make(root), used };
}

// Tidy-tree vertical placement in "slot units". Leaf games take one unit,
// direct entries (byes / drop-ins) take `virtual` units, parents sit at the
// midpoint of their two feeders. Returns total height in units.
export function layoutUnits(root, out, { virtual = 0.5, rootGap = 0 } = {}) {
  let cur = 0;
  const walk = (node, isRoot) => {
    let y;
    if (!node.kids.some(Boolean)) {
      y = cur + 0.5;
      cur += 1;
    } else {
      const ys = node.kids.map((k, i) => {
        if (isRoot && i === 1) cur += rootGap;
        if (k) return walk(k, false);
        const v = cur + virtual / 2;
        cur += virtual;
        return v;
      });
      y = (ys[0] + ys[1]) / 2;
    }
    out.set(node.match.id, { node, y });
    return y;
  };
  walk(root, true);
  return cur;
}
